import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  Select,
  FormHelperText,
  MenuItem,
  TextField,
  CircularProgress,
  Avatar,
  Button,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik, FieldArray } from "formik";
import { IMAGE_BASE } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { AddCircle, RemoveCircle } from "@mui/icons-material";
import errorHandler from "../../utils/errorHandler";
import { CategorySchema } from "../../utils/Schema";
import { apiCategory } from "../../services/AllServices";

function AddEditCategory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [data, setData] = useState();
  const ALLOWED_CHARACTERS_REGEX = /^[A-Za-z0-9\s]*$/;
  const handleKeyPress = (e) => {
    if (!ALLOWED_CHARACTERS_REGEX.test(e.key)) {
      e.preventDefault();
    }
  };
  const { id } = useParams();

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await apiCategory.getById({ id });
        const resData = response?.data?.data;
        setImagePreview(IMAGE_BASE + resData?.image);
        setData(resData);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: CategorySchema,
    initialValues: {
      name: data?.name || "",
      image: data?.image || "",
      status: data?.status || "active",
      isPrimary: data?.isPrimary || false,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();

        if (id) {
          formData.append("id", id);
        }
        formData.append("name", values.name);
        formData.append("status", values.status);
        formData.append("imagePath", "category");
        formData.append("isPrimary", values.isPrimary);
        if (selectedImage && selectedImage !== data?.image) {
          formData.append("image", selectedImage);
        }

        const response = id
          ? await apiCategory.edit(formData)
          : await apiCategory.add(formData);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        console.error(error);
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleImageChange = (event) => {
    const file = event.currentTarget.files[0];
    const validTypes = ["image/jpeg", "image/png", "image/gif"];

    if (selectedImage && selectedImage.name === file?.name) {
      toast.error("You cannot upload the same image again.");
      fileInputRef.current.value = "";
      return;
    }

    if (file && validTypes.includes(file.type)) {
      setImageLoading(true);
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      formik.setFieldValue("image", file);
      setTimeout(() => {
        setImageLoading(false);
      }, 2000);
    } else {
      toast.error("Please upload a valid image (JPEG, PNG, or GIF).");
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box>
      {/* <Helmet> */}
      <title>{id ? "Edit" : "Add"} Category</title>
      {/* </Helmet> */}
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Category`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <input
                    accept="image/*"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />

                  <Box
                    mt={2}
                    onClick={() => fileInputRef.current.click()}
                    className="upload-box"
                  >
                    {imageLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Avatar
                        src={imagePreview || "/path/to/default-avatar.jpg"}
                        alt="preview"
                        className="avatar"
                      />
                    )}
                  </Box>
                  {formik.touched.image && formik.errors.image ? (
                    <div className="error-msg error-category">
                      {formik.errors.image}
                    </div>
                  ) : null}

                  <Box mt={2} display="flex" justifyContent="center">
                    <Button
                      size="large"
                      type="button"
                      variant="contained"
                      onClick={() => fileInputRef.current.click()}
                    >
                      {id ? "Edit" : "Upload"} Image
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    label="Name"
                    // onPaste={(e) => {
                    //   const pastedText = e.clipboardData.getData("text");
                    //   if (/^\d+$/.test(pastedText)) {
                    //     e.preventDefault();
                    //   }
                    // }}
                    // onKeyPress={handleKeyPress}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    name="name"
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.isPrimary}
                        onChange={formik.handleChange}
                        name="isPrimary"
                        color="primary"
                      />
                    }
                    label="Set as Primary Category"
                  />
                </Grid>
                {id && (
                  <Grid item xs={12} md={12}>
                    <Select
                      labelId="status-label"
                      id="status"
                      value={formik.values.status}
                      onChange={(e) =>
                        formik.setFieldValue("status", e.target.value)
                      }
                      fullWidth
                      error={Boolean(
                        formik.touched.status && formik.errors.status
                      )}
                      displayEmpty
                      defaultValue=""
                    >
                      <MenuItem value="">Select Status</MenuItem>
                      <MenuItem value={"active"}>Active</MenuItem>
                      <MenuItem value={"inactive"}>Inactive</MenuItem>
                    </Select>
                    {formik.touched.status && formik.errors.status && (
                      <FormHelperText error>
                        {formik.errors.status}
                      </FormHelperText>
                    )}
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box mt={3} width="100%">
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Category
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditCategory;
